import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { BarChart3, Check, AlertTriangle, XCircle, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BADGE_CONFIG = {
  justified: { bg: '#E8F5E9', border: '#2D8B57', text: '#1B5E20', icon: Check, label: 'Design Justified' },
  marginal: { bg: '#FFF3E0', border: '#E67E22', text: '#E65100', icon: AlertTriangle, label: 'Marginal Benefit' },
  inferior: { bg: '#FFEBEE', border: '#E74C3C', text: '#B71C1C', icon: XCircle, label: 'RBD Would Be Better' },
  baseline: { bg: '#E3F2FD', border: '#1565C0', text: '#0D47A1', icon: Info, label: 'RBD Is The Baseline' },
  info: { bg: '#F3E5F5', border: '#8E44AD', text: '#4A148C', icon: Info, label: 'No Blocking in CRD' },
};

export function EfficiencyComparator({ analysisId, designLabel, designColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchEfficiency = async () => {
    if (data) { setOpen(!open); return; }
    setLoading(true);
    setOpen(true);
    try {
      const res = await axios.get(`${API}/design-efficiency/${analysisId}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Efficiency comparison failed');
      setOpen(false);
    }
    setLoading(false);
  };

  if (!analysisId) return null;

  const badge = data ? BADGE_CONFIG[data.badge] || BADGE_CONFIG.info : null;
  const maxMSE = data ? Math.max(data.design_mse || 0, data.rbd_mse || 0, 1) : 1;

  return (
    <div className="mt-3" data-testid="efficiency-comparator">
      <button onClick={fetchEfficiency}
        className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border"
        style={{ borderColor: (designColor || '#2D8B57') + '40', color: designColor || '#2D8B57', backgroundColor: (designColor || '#2D8B57') + '08' }}
        data-testid="compare-rbd-btn">
        {loading ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
        {open && data ? 'Hide Comparison' : 'Compare with RBD'}
      </button>

      {open && (loading ? (
        <div className="mt-3 p-4 text-center rounded-xl border border-[#E8E6E1] bg-white">
          <Loader2 size={20} className="animate-spin text-[#2D8B57] mx-auto" />
          <p className="text-xs text-[#888] mt-2">Computing efficiency...</p>
        </div>
      ) : data ? (
        <div className="mt-3 rounded-xl border border-[#E8E6E1] bg-white overflow-hidden" data-testid="efficiency-panel">
          {/* Badge */}
          {badge && (
            <div className="px-4 py-2.5 flex items-center gap-2"
              style={{ backgroundColor: badge.bg, borderBottom: `1px solid ${badge.border}30` }}>
              <badge.icon size={14} style={{ color: badge.text }} />
              <span className="text-xs font-bold" style={{ color: badge.text }}>{badge.label}</span>
              {data.re_percent != null && (
                <span className="ml-auto text-xs font-bold" style={{ color: badge.text }}>
                  RE = {data.re_percent}%
                </span>
              )}
            </div>
          )}

          {/* Two cards side by side */}
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Current Design card */}
              <div className="rounded-xl border-2 p-4" style={{ borderColor: designColor || '#2D8B57' }} data-testid="design-card">
                <div className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: designColor || '#2D8B57' }}>
                  Your Design
                </div>
                <div className="text-sm font-bold text-[#2C2A28]">{data.design_name}</div>
                {data.design_mse != null && (
                  <div className="mt-3">
                    <div className="text-[10px] text-[#8A8580] mb-1">Error MSE</div>
                    <div className="text-xl font-bold text-[#2C2A28]">{data.design_mse}</div>
                    {/* Bar */}
                    <div className="mt-2 h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.max((data.design_mse / maxMSE) * 100, 5)}%`,
                          backgroundColor: designColor || '#2D8B57',
                        }} />
                    </div>
                  </div>
                )}
              </div>

              {/* RBD card */}
              <div className="rounded-xl border-2 border-[#9E9E9E] p-4" data-testid="rbd-card">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#9E9E9E] mb-1">
                  Theoretical Baseline
                </div>
                <div className="text-sm font-bold text-[#2C2A28]">{data.rbd_name}</div>
                {data.rbd_mse != null ? (
                  <div className="mt-3">
                    <div className="text-[10px] text-[#8A8580] mb-1">Error MSE</div>
                    <div className="text-xl font-bold text-[#2C2A28]">{data.rbd_mse}</div>
                    <div className="mt-2 h-3 bg-[#f0f0f0] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#9E9E9E] transition-all duration-700"
                        style={{ width: `${Math.max((data.rbd_mse / maxMSE) * 100, 5)}%` }} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-[#888]">Not computable for this design type</div>
                )}
              </div>
            </div>

            {/* Interpretation */}
            <div className="bg-[#F9F8F6] rounded-lg p-3 border border-[#E8E6E1]" data-testid="efficiency-interpretation">
              <p className="text-xs text-[#5E5A56] leading-relaxed">{data.interpretation}</p>
              {data.trait && <p className="text-[10px] text-[#8A8580] mt-1">Trait analyzed: {data.trait}</p>}
            </div>
          </div>
        </div>
      ) : null)}
    </div>
  );
}
